import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusIcon, TrashIcon, WrenchIcon, TimerIcon, HeartbeatIcon } from "@phosphor-icons/react";

import FormattedNumberInput from "@/components/FormattedNumberInput";
const inp = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";
const selCls = "bg-[var(--bg-0)] border-[var(--border)] h-9 text-sm";
const TYPES = ["Preventive", "Breakdown", "Predictive", "Overhaul"];
const STATUSES = ["Scheduled", "In Progress", "Completed", "Cancelled"];

const KPI = ({ label, value, unit, icon: Icon, color = "" }) => (
  <div className="tactical-card p-4">
    <div className="flex items-start justify-between mb-3">
      <div className="overline">{label}</div>
      {Icon && (
        <div className="w-7 h-7 rounded-sm bg-[var(--bg-2)] flex items-center justify-center">
          <Icon size={14} className={color || "text-white"} weight="bold" />
        </div>
      )}
    </div>
    <div className={`font-mono text-2xl font-semibold ${color}`}>
      {value}{unit && <span className="text-sm text-zinc-500 ml-1">{unit}</span>}
    </div>
  </div>
);

export default function Maintenance() {
  const [rows, setRows] = useState([]);
  const [machines, setMachines] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const empty = {
    machine_id: "", maintenance_type: "Preventive",
    scheduled_date: new Date().toISOString().slice(0, 10),
    completed_date: "", duration_min: 0, description: "",
    parts_used: "", technician: "", cost: 0, status: "Scheduled",
  };
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [m, mach, k] = await Promise.all([
      api.get("/maintenance/"),
      api.get("/machines/"),
      api.get("/dashboard/maintenance-kpis"),
    ]);
    setRows(m.data); setMachines(mach.data); setKpis(k.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.machine_id) return toast.error("Machine required");
    try {
      if (editing) {
        await api.put(`/maintenance/${editing.id}`, form);
        toast.success("Updated");
      } else {
        await api.post("/maintenance/", form);
        toast.success("Scheduled");
      }
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const remove = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/maintenance/${id}`);
    toast.success("Deleted"); load();
  };
  const openEdit = (r) => { setEditing(r); setForm({ ...empty, ...r }); setOpen(true); };

  const machineCode = (id) => machines.find(m => m.id === id)?.code || "—";

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline mb-1">Reliability</div>
          <h1 className="font-display text-3xl font-black tracking-tight">Maintenance Management</h1>
        </div>
        <button data-testid="new-mnt-btn" onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="h-10 px-4 bg-[var(--accent)] text-white text-sm font-semibold rounded-sm flex items-center gap-2">
          <PlusIcon size={14} weight="bold" />Schedule Maintenance
        </button>
      </div>

      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <KPI label="Total" value={kpis.total} icon={WrenchIcon} />
          <KPI label="Scheduled" value={kpis.scheduled} icon={TimerIcon} color="text-amber-400" />
          <KPI label="In Progress" value={kpis.in_progress} icon={WrenchIcon} color="text-blue-400" />
          <KPI label="Completed" value={kpis.completed} icon={WrenchIcon} color="text-emerald-400" />
          <KPI label="MTTR" value={kpis.mttr_min} unit="min" icon={TimerIcon} />
          <KPI label="MTBF" value={kpis.mtbf_hours} unit="h" icon={HeartbeatIcon} color="text-emerald-400" />
        </div>
      )}

      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className="text-left px-5 py-3 font-medium">Machine</th>
              <th className="text-left px-5 py-3 font-medium">Type</th>
              <th className="text-left px-5 py-3 font-medium">Scheduled</th>
              <th className="text-left px-5 py-3 font-medium">Completed</th>
              <th className="text-right px-5 py-3 font-medium">Duration (min)</th>
              <th className="text-left px-5 py-3 font-medium">Description</th>
              <th className="text-left px-5 py-3 font-medium">Technician</th>
              <th className="text-right px-5 py-3 font-medium">Cost</th>
              <th className="text-left px-5 py-3 font-medium">Status</th>
              <th className="text-right px-5 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-b border-[var(--border)]" data-testid={`mnt-${r.id}`}>
                <td className="px-5 py-3 font-mono">{machineCode(r.machine_id)}</td>
                <td className="px-5 py-3">{r.maintenance_type}</td>
                <td className="px-5 py-3 font-mono text-zinc-400">{r.scheduled_date}</td>
                <td className="px-5 py-3 font-mono text-zinc-400">{r.completed_date || "—"}</td>
                <td className="px-5 py-3 text-right font-mono">{r.duration_min}</td>
                <td className="px-5 py-3 text-zinc-400">{r.description}</td>
                <td className="px-5 py-3 text-zinc-400">{r.technician}</td>
                <td className="px-5 py-3 text-right font-mono">₹{r.cost}</td>
                <td className="px-5 py-3"><StatusBadge status={r.status === "Scheduled" ? "Planned" : r.status === "Completed" ? "Completed" : r.status === "Cancelled" ? "Cancelled" : "Running"} /></td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => openEdit(r)} className="px-2 text-xs text-[var(--accent)]">Edit</button>
                  <button onClick={() => remove(r.id)} className="p-1.5 rounded-sm hover:bg-red-500/10 text-red-400"><TrashIcon size={14} /></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={10} className="text-center py-16 text-zinc-500">No maintenance records — click Schedule Maintenance.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-2xl" description="Maintenance action — schedule or log a machine maintenance activity.">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit" : "Schedule"} Maintenance</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <F label="Machine">
              <Select value={form.machine_id} onValueChange={v => setForm({ ...form, machine_id: v })}>
                <SelectTrigger data-testid="mnt-machine" className={selCls}><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                  {machines.map(m => <SelectItem key={m.id} value={m.id}>{m.code} · {m.status}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Type">
              <Select value={form.maintenance_type} onValueChange={v => setForm({ ...form, maintenance_type: v })}>
                <SelectTrigger className={selCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                  {TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Scheduled Date"><input type="date" value={form.scheduled_date} onChange={e => setForm({ ...form, scheduled_date: e.target.value })} className={inp} /></F>
            <F label="Completed Date"><input type="date" value={form.completed_date} onChange={e => setForm({ ...form, completed_date: e.target.value })} className={inp} /></F>
            <F label="Duration (min)"><FormattedNumberInput value={form.duration_min} onChange={e => setForm({ ...form, duration_min: parseFloat(e.target.value) || 0 })} className={inp} /></F>
            <F label="Cost (₹)"><FormattedNumberInput value={form.cost} onChange={e => setForm({ ...form, cost: parseFloat(e.target.value) || 0 })} className={inp} /></F>
            <F label="Technician"><input value={form.technician} onChange={e => setForm({ ...form, technician: e.target.value })} className={inp} /></F>
            <F label="Status">
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="mnt-status" className={selCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                  {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Description" className="col-span-2"><input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className={inp} /></F>
            <F label="Parts Used" className="col-span-2"><input value={form.parts_used} onChange={e => setForm({ ...form, parts_used: e.target.value })} className={inp} /></F>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Cancel</button>
            <button data-testid="save-mnt" onClick={save} className="px-4 py-2 rounded-sm bg-[var(--accent)] text-sm font-semibold text-white">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children, className = "" }) {
  return <div className={className}><div className="overline mb-1.5">{label}</div>{children}</div>;
}
